import React, { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { THERAPY_CODES, GOAL_PROGRESS, MINUTES_PER_UNIT, TIMED_UNITS_PER_DAY } from '../../../../../constants/therapyCodes';
import type { TherapyCodeEntry } from '../../../../../constants/therapyCodes';
import { disciplineForProviderType, isAssistantProviderType, therapyDisciplines } from '../../../../../constants/disciplines';
import { CarePlansService } from '../../../../../services/patientmr/careplans.service';
import type { TherapyFormData, TherapyFormLine, TherapyFormGoal } from '../../types';
import { codeLabel } from '../../model/entryMeta';
import { FieldsProps, InputField, Segmented, SelectField, TextAreaField } from './fieldParts';

/** Codes of a therapy discipline, optionally of some kinds */
export const therapyCodesFor = (discipline: string, kinds?: ReadonlyArray<TherapyCodeEntry['kind']>): TherapyCodeEntry[] =>
  THERAPY_CODES.filter((c) => c.discipline === discipline && (!kinds || kinds.includes(c.kind)));

export const therapyCodeOf = (code: string, discipline?: string): TherapyCodeEntry | undefined =>
  THERAPY_CODES.find((c) => c.code === code && (!discipline || c.discipline === discipline));

/** Whole 15-minute units for a timed line (remainder dropped, plan D4) */
export const unitsForMinutes = (minutes: string): number => {
  const n = Number(minutes);
  return Number.isFinite(n) && n > 0 ? Math.floor(n / MINUTES_PER_UNIT) : 0;
};

const codeOption = (c: TherapyCodeEntry) => ({ value: c.code, label: `${c.code} ${c.label}` });

interface PlanGoal { goalId: string; text: string }

/** Active structured goals of the discipline's newest complete care plan ([] when none or on error) */
export async function loadTherapyPlanGoals(patientId: string, discipline: string): Promise<PlanGoal[]> {
  const { carePlans } = await CarePlansService.getCarePlans(patientId);
  const mine = carePlans
    .filter((p) => String(CarePlansService.mapUiDisciplineToApiDiscipline(p.discipline)).toUpperCase() === discipline
      && ['COMPLETE', 'CURRENT', 'SUBMITTED', 'QA'].includes(String(p.status).toUpperCase()))
    .sort((a, b) => Date.parse(b.date) - Date.parse(a.date));
  if (!mine.length) return [];
  const detail = await CarePlansService.getCarePlanDetails(patientId, mine[0].carePlanId);
  const goals = (detail?.data as { goals?: unknown })?.goals;
  if (!Array.isArray(goals)) return [];
  return goals
    .filter((g): g is { goalId: string; text: string; status?: string } => Boolean(g && typeof g === 'object' && (g as { text?: unknown }).text))
    .filter((g) => !g.status || g.status === 'ACTIVE' || g.status === 'PARTIALLY_MET')
    .map((g) => ({ goalId: String(g.goalId || ''), text: String(g.text) }));
}

const PROGRESS_LABELS: Record<string, string> = { PROGRESSING: 'Progressing', MET: 'Met', NO_CHANGE: 'No change', REGRESSED: 'Regressed' };

/**
 * Phase 20C treatment note: the evaluation code (licensed therapists), procedure lines with
 * minutes and units, goals addressed with progress, home programme, response, co-treatment and
 * telehealth. `discipline` is the visit's (PT, OT, ST or an assistant type).
 */
const TherapyTreatmentFields: React.FC<FieldsProps<TherapyFormData> & { discipline?: string | null; patientId?: string }> = ({
  value, onChange, errors, showRequired, isMobile, disabled, discipline, patientId,
}) => {
  const mapped = disciplineForProviderType(discipline) || '';
  const therapyDiscipline = therapyDisciplines().includes(mapped) ? mapped : '';
  const assistant = isAssistantProviderType(discipline);
  const [planGoals, setPlanGoals] = useState<PlanGoal[]>([]);

  useEffect(() => {
    if (!patientId || !therapyDiscipline) return undefined;
    let cancelled = false;
    loadTherapyPlanGoals(patientId, therapyDiscipline)
      .then((g) => { if (!cancelled) setPlanGoals(g); })
      .catch(() => { if (!cancelled) setPlanGoals([]); });
    return () => { cancelled = true; };
  }, [patientId, therapyDiscipline]);

  const common = { isMobile, disabled, showRequired, labelClassName: 'text-emerald-700' };
  const set = <K extends keyof TherapyFormData>(field: K) => (v: TherapyFormData[K]) => onChange({ ...value, [field]: v });
  const setLine = (i: number, patch: Partial<TherapyFormLine>) =>
    onChange({ ...value, lines: value.lines.map((l, j) => (j === i ? { ...l, ...patch } : l)) });
  const setGoal = (i: number, patch: Partial<TherapyFormGoal>) =>
    onChange({ ...value, goalsAddressed: value.goalsAddressed.map((g, j) => (j === i ? { ...g, ...patch } : g)) });

  const evalCodes = therapyCodesFor(therapyDiscipline, ['EVAL', 'REEVAL']);
  const treatmentCodes = therapyCodesFor(therapyDiscipline, ['TIMED', 'SESSION']);
  const timedUnits = value.lines.reduce((n, l) => n + (therapyCodeOf(l.code, therapyDiscipline)?.kind === 'TIMED' ? unitsForMinutes(l.minutes) : 0), 0);

  if (!therapyDiscipline) {
    return <p className="text-sm text-gray-600" data-testid="therapy-not-allowed">A treatment note can only be added on a PT, OT or ST visit.</p>;
  }
  return (
    <div className="space-y-3" data-testid="therapy-treatment-fields">
      {!assistant && (
        <SelectField {...common} id="tx-eval" label="Evaluation or re-evaluation (if billed today)"
          placeholder="None"
          options={evalCodes.map(codeOption)}
          value={value.evaluationCode} onChange={set('evaluationCode')} error={errors.evaluationCode} />
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-emerald-700">Procedures</span>
          <span className="text-xs text-gray-600" data-testid="tx-timed-units">
            {timedUnits} timed unit{timedUnits === 1 ? '' : 's'}
            {timedUnits > TIMED_UNITS_PER_DAY ? ` (Medicaid pays at most ${TIMED_UNITS_PER_DAY} per day)` : ''}
          </span>
        </div>
        {value.lines.map((line, i) => {
          const code = therapyCodeOf(line.code, therapyDiscipline);
          const timed = code?.kind === 'TIMED';
          return (
            <div key={i} className="grid grid-cols-[1fr_auto] items-end gap-2 md:grid-cols-[1fr_8rem_4rem_auto]" data-testid={`tx-line-${i}`}>
              <SelectField {...common} id={`tx-line-${i}-code`} label="Code" options={treatmentCodes.map(codeOption)}
                value={line.code} onChange={(v) => setLine(i, { code: v })} error={errors[`lines[${i}].code`]} />
              <InputField {...common} id={`tx-line-${i}-minutes`} label={timed ? 'Minutes' : 'Minutes (optional)'} type="number" inputMode="numeric"
                required={timed} value={line.minutes} onChange={(v) => setLine(i, { minutes: v })} error={errors[`lines[${i}].minutes`]} />
              <span className="hidden pb-1 text-xs text-gray-600 md:block">{timed ? `${unitsForMinutes(line.minutes)} u` : code ? '1 u' : ''}</span>
              <button type="button" aria-label={`Remove procedure ${i + 1}`} disabled={disabled}
                className="mb-0.5 flex h-11 w-11 shrink-0 items-center justify-center rounded text-gray-500 hover:bg-gray-100 hover:text-red-600"
                onClick={() => onChange({ ...value, lines: value.lines.filter((_, j) => j !== i) })}>
                <Trash2 size={16} />
              </button>
            </div>
          );
        })}
        <button type="button" disabled={disabled} data-testid="tx-add-line"
          className="inline-flex items-center gap-1 rounded border border-emerald-400 min-h-[44px] px-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
          onClick={() => onChange({ ...value, lines: [...value.lines, { code: '', minutes: '' }] })}>
          <Plus size={14} /> Add procedure
        </button>
        {errors.lines && <p className="text-xs text-red-600" data-testid="error-tx-lines">{errors.lines}</p>}
      </div>

      <TextAreaField {...common} id="tx-interventions" label="Interventions" rows={2} value={value.interventions} onChange={set('interventions')} />

      <div className="space-y-2">
        <span className="text-xs font-medium text-emerald-700">Goals addressed</span>
        {value.goalsAddressed.map((g, i) => (
          <div key={i} className="grid grid-cols-1 gap-2 rounded border border-gray-200 p-2 md:grid-cols-[1fr_10rem_auto]" data-testid={`tx-goal-${i}`}>
            {planGoals.length ? (
              <SelectField {...common} id={`tx-goal-${i}-goal`} label="Goal" options={planGoals.map((p) => ({ value: p.goalId || p.text, label: p.text }))}
                value={g.goalId || g.goal}
                onChange={(v) => {
                  const picked = planGoals.find((p) => (p.goalId || p.text) === v);
                  setGoal(i, { goalId: picked?.goalId || '', goal: picked?.text || v });
                }} />
            ) : (
              <InputField {...common} id={`tx-goal-${i}-goal`} label="Goal" value={g.goal} onChange={(v) => setGoal(i, { goal: v, goalId: '' })} />
            )}
            <SelectField {...common} id={`tx-goal-${i}-progress`} label="Progress" required
              options={GOAL_PROGRESS.map((p) => ({ value: p, label: PROGRESS_LABELS[p] || codeLabel(p) }))}
              value={g.progress} onChange={(v) => setGoal(i, { progress: v })} error={errors[`goalsAddressed[${i}].progress`]} />
            <button type="button" aria-label={`Remove goal ${i + 1}`} disabled={disabled}
              className="flex h-11 w-11 shrink-0 items-center justify-center self-end rounded text-gray-500 hover:bg-gray-100 hover:text-red-600"
              onClick={() => onChange({ ...value, goalsAddressed: value.goalsAddressed.filter((_, j) => j !== i) })}>
              <Trash2 size={16} />
            </button>
            <InputField {...common} id={`tx-goal-${i}-note`} label="Measurable progress" className="md:col-span-3"
              placeholder="e.g. walked 35 ft with walker, min assist" value={g.note} onChange={(v) => setGoal(i, { note: v })} />
          </div>
        ))}
        <button type="button" disabled={disabled} data-testid="tx-add-goal"
          className="inline-flex items-center gap-1 rounded border border-emerald-400 min-h-[44px] px-3 text-sm font-medium text-emerald-700 hover:bg-emerald-50"
          onClick={() => onChange({ ...value, goalsAddressed: [...value.goalsAddressed, { goal: '', goalId: '', progress: '', note: '' }] })}>
          <Plus size={14} /> Add goal
        </button>
      </div>

      <TextAreaField {...common} id="tx-home-program" label="Home programme given" rows={2} value={value.homeProgram} onChange={set('homeProgram')} />
      <TextAreaField {...common} id="tx-response" label="Patient / caregiver response" rows={2} value={value.patientResponse} onChange={set('patientResponse')} />
      <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
        <Segmented name="tx-cotreat" label="Co-treatment" isMobile={isMobile} disabled={disabled}
          options={[{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }]}
          value={value.coTreatment ? 'YES' : 'NO'} onChange={(v) => onChange({ ...value, coTreatment: v === 'YES' })}
          showRequired={showRequired} />
        <Segmented name="tx-telehealth" label="Telehealth (synchronous)" isMobile={isMobile} disabled={disabled}
          options={[{ value: 'YES', label: 'Yes' }, { value: 'NO', label: 'No' }]}
          value={value.telehealth ? 'YES' : 'NO'} onChange={(v) => onChange({ ...value, telehealth: v === 'YES' })}
          showRequired={showRequired} />
      </div>
      {value.coTreatment && (
        <InputField {...common} id="tx-cotreat-with" label="Co-treating discipline or clinician" required
          value={value.coTreatmentWith} onChange={set('coTreatmentWith')} error={errors.coTreatmentWith} />
      )}
    </div>
  );
};

export default TherapyTreatmentFields;
